import React, { Component } from 'react';
import './SaveValuationButton.css';
import ApiHandler from "src/api/ApiHandler";
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler";
import StateConstants from "src/states/StateContstants";
import ValuationStateHelper from "src/utils/ValuationStateHelper";
import LoginHelper from "src/components/login/LoginHelper";
import RedirectToLoginException from "src/validation/exception_messages/RedirectToLoginException";
import Hasher from "src/utils/Hasher";
import posthog from "posthog-js";


interface SaveValuationButtonProps {
    apiHandler: ApiHandler;
    stateHandler: EventAdaptingStateHandler;
    loginHelper: LoginHelper;
    valuationId: string; 
    onSaveSuccess?: () => void;
    onSaveError?: (error: string) => void;
    isSingleValuationPage: boolean;
}

interface SaveValuationButtonState {
    isSaving: boolean;
    isSaved: boolean;
    saveMessage: string | null;
}

class SaveValuationButton extends Component<SaveValuationButtonProps, SaveValuationButtonState> {
    
    constructor(props: SaveValuationButtonProps) {
        super(props);
        this.state = {
            isSaving: false,
            isSaved: false,
            saveMessage: null
        };
        this.props.stateHandler.addListener(StateConstants.VALUATION_STATE, (state: any) => {
            this.checkIfSaved();
        });
    }

    componentDidMount() {
        // Check if this valuation is already saved
        this.checkIfSaved();
    }

    checkIfSaved = () => {
        const { valuationId, stateHandler } = this.props;
        
        if (!this.props.isSingleValuationPage) {
            this.setState({ isSaved: true });
            return;
        } 
        // For house form - check if current valuation is saved
        const comparableHomes = stateHandler.get(StateConstants.COMPARABLE_HOMES_STATE);
        const removedHomes = stateHandler.get(StateConstants.REMOVED_HOMES);
        const houseCondition = stateHandler.get(StateConstants.RELATIVE_CONDITION);
        if (comparableHomes && comparableHomes["address"]) {
            const currentAddressId = comparableHomes["address"]["id"];
            const savedValuations = stateHandler.get(StateConstants.SAVED_VALUATIONS);
            if (savedValuations && savedValuations["i"]) {
                const isSaved = savedValuations["i"].some((savedValuation: any) => 
                    savedValuation.a.id === currentAddressId 
                    && parseFloat(savedValuation.a.rc) === parseFloat(houseCondition)
                    && Hasher.listsEqualUnordered(savedValuation.rn || [], removedHomes || [])
                );
                this.setState({ isSaved });
            }
        }
    };

    saveValuation = async () => {
        const { apiHandler, loginHelper, stateHandler, valuationId, onSaveSuccess, onSaveError } = this.props;
        
        try {
            this.setState({ isSaving: true, saveMessage: null });
            
            // Check if user is logged in
            const isLoggedIn = stateHandler.get(StateConstants.IS_LOGGED_IN);
            if (!isLoggedIn) {
                stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true);
                return;
            }

            if (this.props.isSingleValuationPage) {
                // For house form - save new valuation
                const comparableHomes = stateHandler.get(StateConstants.COMPARABLE_HOMES_STATE);
                const removedHomes = stateHandler.get(StateConstants.REMOVED_HOMES);
                console.log("removedHomes", removedHomes);
                
                if (!comparableHomes || !comparableHomes["address"]) {
                    throw new Error("No address information available");
                }

                let response = await apiHandler.saveValuation({
                    address: comparableHomes["address"],
                    removed_neighbours: removedHomes
                });
                ValuationStateHelper.addSavedValuation(stateHandler, response.saved_valuation);
            }

            posthog?.capture('valuation_saved', { valuation_id: valuationId });
            this.setState({
                isSaved: true,
                isSaving: false,
                saveMessage: "Valuation saved successfully!"
            });

        } catch (error) {
            if (error instanceof RedirectToLoginException) {
                // User needs to login, redirect will be handled by ApiHandler
                return;
            }
            
            const errorMsg = error instanceof Error ? error.message : "Failed to save valuation. Please try again.";
            this.setState({ 
                isSaved: false,
                isSaving: false,
                saveMessage: errorMsg
            });
        }
    };

    deleteValuation = async () => {
        this.setState({ isSaved: false });
        const { apiHandler, valuationId, onSaveSuccess } = this.props;
        if (!valuationId) {
            return;
        }
        posthog?.capture('valuation_deleted', { valuation_id: valuationId });
        ValuationStateHelper.removeSavedValuation(this.props.stateHandler, valuationId);
        await apiHandler.deleteValuation({ valuation_id: valuationId });
    }

    render() {
        const { isSaving, isSaved } = this.state;
        
        return (
            <div className="save-valuation-button-container">
                <button 
                    className={`save-valuation-button ${isSaved ? 'saved' : ''} ${isSaving ? 'saving' : ''}`}
                    onClick={isSaved ? this.deleteValuation : this.saveValuation}
                    title={isSaved ? "Valuation saved" : "Save valuation"}
                    aria-label={isSaved ? "Valuation saved" : "Save valuation"}
                >
                    <HeartIcon isSaved={isSaved} />
                </button>
            </div>
        );
    }
}

interface HeartIconProps {
    isSaved: boolean;
}

class HeartIcon extends Component<HeartIconProps> {
    constructor(props: HeartIconProps) {
        super(props);
    }

    render() {
        return (
            <svg
                className="bookmark-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill={this.props.isSaved ? "#FF746C" : "white"}
                stroke={this.props.isSaved ? "#FF746C" : "black"}
                strokeWidth="1"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
        );
    }
}

export default SaveValuationButton;
